import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

interface DashboardStats {
  totalPatients: number
  activePatients: number
  totalReports: number
  reportsThisWeek: number
  totalInsights: number
  pendingInsights: number
}

async function fetchDashboardStats(organizationId: string): Promise<DashboardStats> {
  const now = new Date()
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

  // Fetch patients count (exclude deleted)
  const { count: totalPatients } = await supabase
    .from('patients')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .neq('status', 'deleted')

  const { count: activePatients } = await supabase
    .from('patients')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('status', 'active')

  // First get patient IDs for this organization (exclude deleted)
  const { data: orgPatients } = await supabase
    .from('patients')
    .select('id')
    .eq('organization_id', organizationId)
    .neq('status', 'deleted')

  const patientIds = orgPatients?.map(p => p.id) ?? []

  // Fetch reports count - daily_reports links to organization through patients
  let totalReports = 0
  let reportsThisWeek = 0
  if (patientIds.length > 0) {
    const { count } = await supabase
      .from('daily_reports')
      .select('*', { count: 'exact', head: true })
      .in('patient_id', patientIds)
    totalReports = count ?? 0

    const { count: weekCount } = await supabase
      .from('daily_reports')
      .select('*', { count: 'exact', head: true })
      .in('patient_id', patientIds)
      .gte('created_at', weekAgo.toISOString())
    reportsThisWeek = weekCount ?? 0
  }

  // Fetch insights count - use inner join to filter by organization through patients
  const { count: totalInsightsCount } = await supabase
    .from('key_insights')
    .select('*, patients!inner(organization_id, status)', { count: 'exact', head: true })
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
  const totalInsights = totalInsightsCount ?? 0

  const { count: pendingCount } = await supabase
    .from('key_insights')
    .select('*, patients!inner(organization_id, status)', { count: 'exact', head: true })
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .is('acknowledged_at', null)
  const pendingInsights = pendingCount ?? 0

  return {
    totalPatients: totalPatients ?? 0,
    activePatients: activePatients ?? 0,
    totalReports,
    reportsThisWeek,
    totalInsights,
    pendingInsights,
  }
}

export function useDashboardStats() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['dashboard-stats', organizationId],
    queryFn: () => fetchDashboardStats(organizationId!),
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

interface RecentActivity {
  id: string
  type: 'report' | 'insight' | 'patient'
  title: string
  description: string
  timestamp: string
  patientId?: string
  patientName?: string
}

async function fetchRecentActivity(organizationId: string): Promise<RecentActivity[]> {
  const activities: RecentActivity[] = []

  // Fetch recent reports - use inner join to filter by organization through patients
  const { data: recentReports } = await supabase
    .from('daily_reports')
    .select(`
      id,
      content,
      created_at,
      patients!inner (
        id,
        full_name,
        organization_id,
        status
      )
    `)
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .order('created_at', { ascending: false })
    .limit(5)

  if (recentReports) {
    for (const report of recentReports) {
      const patient = report.patients as unknown as { id: string; full_name: string } | null
      activities.push({
        id: report.id,
        type: 'report',
        title: 'Novo relatório recebido',
        description: report.content?.substring(0, 100) + '...',
        timestamp: report.created_at,
        patientId: patient?.id,
        patientName: patient?.full_name,
      })
    }
  }

  // Fetch recent insights - use inner join to filter by organization through patients
  const { data: recentInsights } = await supabase
    .from('key_insights')
    .select(`
      id,
      insight_text,
      severity,
      created_at,
      patients!inner (
        id,
        full_name,
        organization_id,
        status
      )
    `)
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .is('acknowledged_at', null)
    .order('created_at', { ascending: false })
    .limit(5)

  if (recentInsights) {
    for (const insight of recentInsights) {
      const patient = insight.patients as unknown as { id: string; full_name: string } | null
      activities.push({
        id: insight.id,
        type: 'insight',
        title: insight.insight_text?.substring(0, 50) + '...',
        description: `Severidade: ${insight.severity}`,
        timestamp: insight.created_at,
        patientId: patient?.id,
        patientName: patient?.full_name,
      })
    }
  }

  // Sort by timestamp
  return activities.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  ).slice(0, 10)
}

export function useRecentActivity() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['recent-activity', organizationId],
    queryFn: () => fetchRecentActivity(organizationId!),
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}
